/**
 * EmailBlocklistPage — Administration › E-Mail-Blockliste
 * (Berechtigungskonzept V3.0 · Bereich 1, 3.5).
 *
 * The 3-tier blocklist. Platform rules are system-managed (read-only for everyone);
 * tenant rules are admin-managed (tenant.manage); user rules are personal (any broker,
 * own accounts only). A lower-tier rule may only TIGHTEN a matching higher-tier rule
 * ("verschärfen ja, lockern nie") — the api rejects a looser rule outright.
 *
 * Mirrors the AusschlusslistePage shape (header + grouped list + create dialog); wired to
 * /email-blocklist via apiClient.emailBlocklist.*.
 *
 * [Source: apps/web/src/features/administration/pages/AusschlusslistePage.tsx] (page + dialog pattern)
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmailAddress } from '@brokly/shared';
import type {
  EmailBlacklistRuleDto,
  EmailBlacklistVisibilityScope,
} from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { mockBlockAddress, mockUnblock } from '../../blocklist/useMyBlocklist';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useEmailBlocklist } from '../hooks';
import { Button, Dialog, LoadingState, ErrorState, EmptyState, TextInput } from '../components';
import { Badge } from '../../../components/ui/Badge';

const meta = ADMIN_PAGE_META.blockliste;

const LEVEL_LABEL: Record<EmailBlacklistRuleDto['level'], string> = {
  platform: 'Plattform',
  tenant: 'Mandant',
  user: 'Persönlich',
};

const SCOPE_LABEL: Record<EmailBlacklistVisibilityScope, string> = {
  never_sync: 'Nie synchronisieren',
  metadata_only: 'Nur Metadaten',
  owner_only: 'Nur für Postfach-Inhaber',
};

const SCOPE_OPTIONS: EmailBlacklistVisibilityScope[] = ['never_sync', 'metadata_only', 'owner_only'];

// ── Create dialog ─────────────────────────────────────────────────────────────
function BlocklistCreateDialog({
  canManageTenant,
  onClose,
}: {
  canManageTenant: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [level, setLevel] = useState<'tenant' | 'user'>(canManageTenant ? 'tenant' : 'user');
  const [pattern, setPattern] = useState('');
  const [scope, setScope] = useState<EmailBlacklistVisibilityScope>('never_sync');
  const [hidePast, setHidePast] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.emailBlocklist() });
  };

  // P2 hardening 04: "bis zu N bisherige E-Mails" for the hide_past checkbox (tenant level only).
  const trimmed = pattern.trim();
  const previewEnabled = level === 'tenant' && trimmed.length >= 3;
  const previewQuery = useQuery({
    queryKey: [...qk.emailBlocklist(), 'preview', trimmed],
    enabled: previewEnabled,
    queryFn: async () => {
      if (!isLiveData()) return { past_count: 3 };
      return apiClient.emailBlocklist.preview(trimmed);
    },
  });
  const pastCount = previewQuery.data?.past_count ?? 0;

  const save = useMutation({
    mutationFn: async () => {
      // Mock mode: a personal rule goes into the shared demo list (features/blocklist).
      if (!isLiveData() && level === 'user') {
        // The api's rule, mirrored for the demo: a personal rule is one address, never a domain.
        if (!isEmailAddress(pattern)) {
          throw new Error('Persönlich kann nur eine einzelne E-Mail-Adresse blockiert werden. Ganze Domains blockiert ein Admin.');
        }
        return mockBlockAddress(pattern);
      }
      return apiClient.emailBlocklist.create({
        level,
        pattern: pattern.trim(),
        visibility_scope: scope,
        ...(level === 'tenant' && hidePast ? { hide_past: true } : {}),
      });
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Regel hinzufügen"
      description="Absender (Domain oder Adresse), die nie oder nur eingeschränkt synchronisiert werden."
      footer={
        <div className="flex flex-1 items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={save.isPending || pattern.trim() === ''} onClick={() => save.mutate()}>
            Speichern
          </Button>
        </div>
      }
    >
      {error != null && <div className="mb-3 text-sm text-status-danger">{error}</div>}
      <div className="space-y-4">
        {canManageTenant && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Ebene</label>
            <div className="flex gap-2">
              <Button
                variant={level === 'tenant' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLevel('tenant')}
              >
                Mandant
              </Button>
              <Button
                variant={level === 'user' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLevel('user')}
              >
                Persönlich
              </Button>
            </div>
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Domain oder Adresse
          </label>
          <TextInput
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="hausarzt.ch oder kanzlei@hausarzt.ch"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Umfang</label>
          <select
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
            value={scope}
            onChange={(e) => setScope(e.target.value as EmailBlacklistVisibilityScope)}
          >
            {SCOPE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {SCOPE_LABEL[s]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            Eine persönliche/Mandanten-Regel darf eine strengere übergeordnete Regel nur
            verschärfen, nie lockern.
          </p>
        </div>
        {level === 'tenant' && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={hidePast}
              onChange={(e) => setHidePast(e.target.checked)}
            />
            <span>
              Auch bisherige E-Mails im Posteingang ausblenden
              {previewEnabled && (
                <span className="text-muted-foreground"> (bis zu {pastCount})</span>
              )}
              <span className="block text-xs text-muted-foreground">
                Bereits erhaltene E-Mails dieses Absenders verschwinden für alle Mitarbeitenden aus
                dem Posteingang. Umkehrbar beim Löschen der Regel.
              </span>
            </span>
          </label>
        )}
      </div>
    </Dialog>
  );
}

export function EmailBlocklistPage() {
  const listQuery = useEmailBlocklist();
  const [showCreate, setShowCreate] = useState(false);
  // P2 hardening 04: the rule whose two-option delete confirm is open (only for hidden_past rules).
  const [confirmDel, setConfirmDel] = useState<EmailBlacklistRuleDto | null>(null);
  const qc = useQueryClient();

  const data = listQuery.data ?? { rules: [], can_manage_tenant: false };
  const rules = data.rules;

  const del = useMutation({
    mutationFn: async (vars: { id: string; reveal_past?: boolean }) => {
      if (!isLiveData()) return mockUnblock(vars.id);
      return apiClient.emailBlocklist.remove(vars.id, { reveal_past: vars.reveal_past });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.emailBlocklist() });
      setConfirmDel(null);
    },
  });

  return (
    <div>
      <AdminPageHeader
        title={meta.title}
        description={meta.description}
        actions={<Button onClick={() => setShowCreate(true)}>Neue Regel</Button>}
      />
      <p className="mb-3 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        Persönliche Ausblendungen heissen neu «Private Absender» und werden unter Einstellungen
        verwaltet.
      </p>
      {listQuery.isLoading ? (
        <LoadingState />
      ) : listQuery.isError ? (
        <ErrorState />
      ) : rules.length === 0 ? (
        <EmptyState label="Noch keine Regeln. Blockiere Absender, die nie oder nur eingeschränkt synchronisiert werden sollen." />
      ) : (
        <ul className="space-y-2">
          {rules.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <span className="truncate">{r.pattern}</span>
                  <Badge variant="neutral">{LEVEL_LABEL[r.level]}</Badge>
                  {r.hidden_past && <Badge variant="neutral">bisherige ausgeblendet</Badge>}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {SCOPE_LABEL[r.visibility_scope]}
                </span>
              </div>
              {r.read_only ? (
                <Badge variant="neutral">Systemverwaltet</Badge>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-status-danger hover:bg-status-danger-bg"
                  disabled={del.isPending}
                  onClick={() => (r.hidden_past ? setConfirmDel(r) : del.mutate({ id: r.id }))}
                >
                  Löschen
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {showCreate && (
        <BlocklistCreateDialog
          canManageTenant={data.can_manage_tenant}
          onClose={() => setShowCreate(false)}
        />
      )}
      {confirmDel && (
        <Dialog
          open
          onClose={() => setConfirmDel(null)}
          title="Regel löschen"
          description={`${confirmDel.pattern}: Bisherige E-Mails dieses Absenders sind firmenweit ausgeblendet. Was soll damit geschehen?`}
          footer={
            <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDel(null)}>
                Abbrechen
              </Button>
              <Button
                variant="outline"
                disabled={del.isPending}
                onClick={() => del.mutate({ id: confirmDel.id, reveal_past: false })}
              >
                Regel löschen, bisherige bleiben ausgeblendet
              </Button>
              <Button
                disabled={del.isPending}
                onClick={() => del.mutate({ id: confirmDel.id, reveal_past: true })}
              >
                Regel löschen und alle wieder anzeigen
              </Button>
            </div>
          }
        >
          <p className="text-sm text-muted-foreground">
            «Bisherige bleiben ausgeblendet» schliesst nur den bisherigen Zeitraum ab; «alle wieder
            anzeigen» macht die Ausblendung vollständig rückgängig.
          </p>
        </Dialog>
      )}
    </div>
  );
}
